/**
 * usePitchMedia
 * Custom hook handling photo, video AND YouTube upload for a member's pitch slot.
 *
 * Rule 5: uploadBytesResumable with progress, validation before upload,
 *         error shown in Dutch, Storage URL saved AFTER successful upload.
 * Rule 6: Logic extracted from NetworkClubMeeting to keep it < 300 lines.
 * Rule 9: All limits and strings from meetingConstants.
 */

import { useState, useCallback } from 'react';
import { storage, db } from '../../../firebase';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import {
  COLLECTIONS,
  STORAGE_PATHS,
  MAX_IMAGE_BYTES,
  MAX_VIDEO_BYTES,
  PHOTO_SOFT_LIMIT,
  ACCEPTED_IMAGE_TYPES,
  ACCEPTED_VIDEO_TYPES,
  STRINGS,
} from '../meetingConstants';
import { compressForThumbnail } from '../../../utils/imageCompressor';

export type MediaItem = {
  id: string;
  type: 'image' | 'video' | 'youtube';
  storageUrl: string;
  thumbnailUrl?: string;
  durationSeconds?: number;   // afspeelduur video of weergaveduur foto
  loop?: boolean;              // video loopt terug als pitch langer duurt
  displaySeconds?: number;    // seconden per foto (instelbaar per lid)
  youtubeUrl?: string;        // originele YouTube URL (type === 'youtube')
  youtubeVideoId?: string;    // YouTube video-ID voor embed
  order: number;
};

interface UsePitchMediaOptions {
  entityId: string;
  roomName: string;
  userId: string;
}

interface UploadState {
  isUploading: boolean;
  progress: number;       // 0-100
  error: string | null;
  softLimitWarning: boolean;
  durationWarning: string | null; // bijv. "Video is langer dan pitch-tijd"
}

const UPLOAD_IDLE: UploadState = {
  isUploading: false,
  progress: 0,
  error: null,
  softLimitWarning: false,
  durationWarning: null,
};

function generateId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Extraheer YouTube video-ID uit diverse URL-formaten */
export function extractYoutubeVideoId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') return u.pathname.slice(1);
    if (u.hostname.endsWith('youtube.com')) {
      const v = u.searchParams.get('v');
      if (v) return v;
      const parts = u.pathname.split('/');
      const shortIdx = parts.indexOf('shorts');
      if (shortIdx !== -1) return parts[shortIdx + 1] ?? null;
      const embedIdx = parts.indexOf('embed');
      if (embedIdx !== -1) return parts[embedIdx + 1] ?? null;
    }
    return null;
  } catch {
    return null;
  }
}

export function usePitchMedia({ entityId, roomName, userId }: UsePitchMediaOptions) {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [uploadState, setUploadState] = useState<UploadState>(UPLOAD_IDLE);

  // ── Persist media list to Firestore ──────────────────────────────────────
  const persistMedia = useCallback(
    async (items: MediaItem[]): Promise<void> => {
      const docId = `${entityId}_${roomName}`;
      await setDoc(
        doc(db, COLLECTIONS.LIVE_PITCHES, docId),
        {
          entityId,
          roomName,
          entries: items,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    },
    [entityId, roomName],
  );

  // ── Lees video-duur uit een File object ───────────────────────────────────
  const extractVideoDuration = (file: File): Promise<number | null> =>
    new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      const objectUrl = URL.createObjectURL(file);
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(isFinite(video.duration) ? Math.round(video.duration) : null);
      };
      video.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(null); };
      video.src = objectUrl;
    });

  // ── Upload een enkel bestand (afbeelding of video) ────────────────────────
  const uploadFile = useCallback(
    async (file: File, opts?: { loop?: boolean; displaySeconds?: number; pitchDurationSeconds?: number }): Promise<void> => {
      const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type);
      const isVideo = ACCEPTED_VIDEO_TYPES.includes(file.type);

      if (!isImage && !isVideo) {
        setUploadState((s) => ({ ...s, error: STRINGS.upload.videoWrongType }));
        return;
      }

      if (isImage && file.size > MAX_IMAGE_BYTES) {
        setUploadState((s) => ({ ...s, error: STRINGS.upload.photoTooLarge }));
        return;
      }
      if (isVideo && file.size > MAX_VIDEO_BYTES) {
        setUploadState((s) => ({ ...s, error: STRINGS.upload.videoTooLarge }));
        return;
      }

      setUploadState({ isUploading: true, progress: 0, error: null, softLimitWarning: false, durationWarning: null });

      // Video-duur controleren vóór upload
      let durationSeconds: number | undefined;
      let durationWarning: string | null = null;
      if (isVideo) {
        const dur = await extractVideoDuration(file);
        if (dur != null) {
          durationSeconds = dur;
          if (opts?.pitchDurationSeconds && dur > opts.pitchDurationSeconds) {
            durationWarning = `Video is ${dur}s — langer dan je pitch (${opts.pitchDurationSeconds}s). De video loopt door tot het einde van je slot.`;
          }
        }
      }

      try {
        let uploadBlob: Blob = file;
        if (isImage) {
          uploadBlob = await compressForThumbnail(file);
        }

        const path = `${STORAGE_PATHS.pitchMedia(entityId, userId)}/${generateId()}_${file.name}`;
        const storageRef = ref(storage, path);
        const uploadTask = uploadBytesResumable(storageRef, uploadBlob);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const pct = Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
              );
              setUploadState((s) => ({ ...s, progress: pct }));
            },
            (error) => { console.error('[usePitchMedia] Upload error:', error); reject(error); },
            () => resolve(),
          );
        });

        const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

        const newItem: MediaItem = {
          id: generateId(),
          type: isImage ? 'image' : 'video',
          storageUrl: downloadUrl,
          order: mediaItems.length,
          ...(durationSeconds !== undefined && { durationSeconds }),
          ...(opts?.loop !== undefined && { loop: opts.loop }),
          ...(opts?.displaySeconds !== undefined && { displaySeconds: opts.displaySeconds }),
        };

        const nextItems = [...mediaItems, newItem];
        setMediaItems(nextItems);

        const softWarning = nextItems.filter((m) => m.type === 'image').length > PHOTO_SOFT_LIMIT;
        setUploadState({
          isUploading: false,
          progress: 100,
          error: null,
          softLimitWarning: softWarning,
          durationWarning,
        });

        await persistMedia(nextItems);
      } catch {
        setUploadState({
          isUploading: false,
          progress: 0,
          error: STRINGS.upload.videoError,
          softLimitWarning: false,
          durationWarning: null,
        });
      }
    },
    [entityId, userId, mediaItems, persistMedia],
  );

  // ── YouTube URL toevoegen (geen upload nodig) ─────────────────────────────
  const addYoutubeLink = useCallback(
    async (youtubeUrl: string, opts?: { loop?: boolean }): Promise<boolean> => {
      const videoId = extractYoutubeVideoId(youtubeUrl);
      if (!videoId) {
        setUploadState((s) => ({ ...s, error: 'Ongeldige YouTube URL. Gebruik een youtube.com of youtu.be link.' }));
        return false;
      }

      const newItem: MediaItem = {
        id: generateId(),
        type: 'youtube',
        storageUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&controls=0`,
        youtubeUrl,
        youtubeVideoId: videoId,
        loop: opts?.loop,
        order: mediaItems.length,
      };

      const nextItems = [...mediaItems, newItem];
      setMediaItems(nextItems);
      setUploadState((s) => ({ ...s, error: null }));
      await persistMedia(nextItems);
      return true;
    },
    [mediaItems, persistMedia],
  );

  // ── Verwijder een media-item ───────────────────────────────────────────────
  const removeItem = useCallback(
    async (itemId: string): Promise<void> => {
      const item = mediaItems.find((m) => m.id === itemId);
      if (!item) return;

      // Alleen Storage-objecten verwijderen — niet voor YouTube
      if (item.type !== 'youtube') {
        try {
          await deleteObject(ref(storage, item.storageUrl));
        } catch (err) {
          console.warn('[usePitchMedia] Storage delete failed (non-critical):', err);
        }
      }

      const nextItems = mediaItems
        .filter((m) => m.id !== itemId)
        .map((m, i) => ({ ...m, order: i }));

      setMediaItems(nextItems);
      await persistMedia(nextItems);
    },
    [mediaItems, persistMedia],
  );

  // ── Update eigenschappen van een bestaand media-item ─────────────────────
  const updateItem = useCallback(
    async (itemId: string, patch: Partial<Pick<MediaItem, 'loop' | 'displaySeconds'>>): Promise<void> => {
      const nextItems = mediaItems.map((m) =>
        m.id === itemId ? { ...m, ...patch } : m,
      );
      setMediaItems(nextItems);
      await persistMedia(nextItems);
    },
    [mediaItems, persistMedia],
  );

  // ── Hydrateer vanuit Firestore bij laden ──────────────────────────────────
  const hydrateFromEntry = useCallback((items: MediaItem[]): void => {
    setMediaItems(items);
  }, []);

  return {
    mediaItems,
    uploadState,
    uploadFile,
    addYoutubeLink,
    removeItem,
    updateItem,
    hydrateFromEntry,
    clearDurationWarning: () => setUploadState((s) => ({ ...s, durationWarning: null })),
  };
}
